import { Notifications, Reminder } from '@griply/types';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { RemindersPopupMenu } from 'shared/components/ui/reminders-popup-menu';
import {
  Colors,
  TaskMetaInteraction,
} from 'shared/components/ui/task-meta-interaction';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useRemindersTrigger } from 'shared/hooks/use-reminders-trigger';
import { TimeFormatOptions } from 'shared/types/date-format-options';

import * as Styled from './reminders-menu.style';

export type RemindersMenuProps = Omit<
  React.ComponentProps<typeof TaskMetaInteraction>,
  'start' | 'onClick' | 'label' | 'selected' | 'tooltipLabel' | 'color'
> & {
  reminders?: Reminder[] | null;
  reminderTime?: number | null;
  hasTime: boolean;
  // Both required for the trigger to show the user's settings default on an empty list.
  notifications?: Notifications;
  hasDate?: boolean;
  timeFormat: TimeFormatOptions;
  highlight?: boolean;
  onChange: (reminders: Reminder[]) => void;
};

export const RemindersMenu: React.FC<RemindersMenuProps> = ({
  reminders,
  reminderTime,
  hasTime,
  notifications,
  hasDate,
  timeFormat,
  highlight,
  onChange,
  ...rest
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const { effectiveReminders, summaryLabel, openWithMigration } =
    useRemindersTrigger({
      reminders,
      reminderTime,
      timeFormat,
      notifications,
      hasDate,
      hasTime,
      onChange,
      openMenu,
    });

  return (
    <Styled.Container ref={containerRef}>
      <TaskMetaInteraction
        start={<Icon icon={Bell} />}
        color={highlight && summaryLabel ? Colors.TextPrimary : Colors.Inherit}
        onClick={openWithMigration}
        label={summaryLabel}
        tooltipLabel={t(
          summaryLabel
            ? 'task.reminder.tooltip.edit.label'
            : 'task.reminder.tooltip.add.label',
        )}
        selected={menuOpen}
        {...rest}
      />

      {menuOpen && (
        <RemindersPopupMenu
          referenceElement={containerRef}
          reminders={effectiveReminders}
          hasTime={hasTime}
          timeFormat={timeFormat}
          onChange={onChange}
          onClose={closeMenu}
        />
      )}
    </Styled.Container>
  );
};
